/**
 * The Rotom Phone's home screen: who the phone belongs to, what is playing, and the apps.
 *
 * It switches the companion on (like Music and the Map), so a returning player sees their soundtrack's state
 * the moment they open the phone; with nothing to renew it asks nobody anything and says how to connect.
 */
import { useState } from 'react';
import { Link } from 'react-router';

import { isStaff } from '@dimensionscore/shared';

import { useSession } from '../auth/SessionProvider';
import { SignInButton } from '../components/SessionBar';
import rotomFace from '../assets/rotom-phone-face.png';
import { useAudioCompanion, type AudioCompanion } from '../audio/AudioCompanionProvider';
import { AreaName, Visualiser } from '../audio/parts';
import { PHONE_APPS, type PhoneApp } from './apps';
import { Glyph } from './icons';

export function PhoneHome(): React.JSX.Element {
  const companion = useAudioCompanion();
  const { session, self } = useSession();
  const staff = session !== null && isStaff(session.claims.role);
  const [soon, setSoon] = useState<string | null>(null);
  const identity = companion.state.phase === 'ready' ? companion.state.identity : null;
  const apps = PHONE_APPS.filter((app) => app.availability !== 'staff' || staff);

  return (
    <section className="ph-home" data-testid="phone-home" aria-labelledby="ph-home-title">
      <header className="ph-home-head">
        <img src={rotomFace} className="ph-rotom" width={36} height={94} alt="" />
        <div>
          <h1 id="ph-home-title" className="ph-home-title">
            Rotom Phone
          </h1>
          <p className="ph-home-sub" data-testid="phone-owner">
            {identity !== null
              ? `${identity.name}'s phone`
              : self !== null
                ? `${self.claims.name}'s phone`
                : session !== null
                  ? session.claims.name
                  : 'Not connected yet'}
          </p>
        </div>
      </header>

      <NowPlaying companion={companion} />

      <ul className="ph-grid" aria-label="Apps">
        {apps.map((app) => (
          <li key={app.id}>
            <AppIcon app={app} onSoon={setSoon} />
          </li>
        ))}
      </ul>
      {soon !== null && (
        <p className="ph-soon-note" role="status" data-testid="phone-soon-note">
          {soon} is coming to the Rotom Phone soon.
        </p>
      )}

      {session === null && (
        <p className="ph-staff-door">
          Staff? <SignInButton returnTo="/" compact testId="phone-staff-sign-in" />
        </p>
      )}
    </section>
  );
}

function AppIcon({ app, onSoon }: { readonly app: PhoneApp; readonly onSoon: (name: string) => void }): React.JSX.Element {
  const face = (
    <>
      <span className={`ph-icon-tile ph-tile-${app.id}`} aria-hidden="true">
        <Glyph name={app.glyph} size={28} />
      </span>
      <span className="ph-icon-name">{app.name}</span>
    </>
  );
  if (app.availability === 'soon' || app.path === null) {
    return (
      <button type="button" className="ph-icon ph-icon-soon" aria-disabled="true" onClick={() => onSoon(app.name)} data-testid={`phone-icon-${app.id}`}>
        {face}
        <span className="ph-soon-badge">Soon</span>
      </button>
    );
  }
  return (
    <Link to={app.path} className="ph-icon" data-testid={`phone-icon-${app.id}`}>
      {face}
    </Link>
  );
}

/** The home screen's media widget: one tap to the Music app, whatever the music is doing. */
function NowPlaying({ companion }: { readonly companion: AudioCompanion }): React.JSX.Element {
  const { state, engine } = companion;
  const { phase, snapshot, enabled } = state;
  const playing = enabled && snapshot.state === 'playing';
  let line: React.ReactNode;
  let detail: string;
  if (phase === 'checking' || phase === 'claiming') {
    line = 'Connecting…';
    detail = 'Looking for your soundtrack.';
  } else if (phase !== 'ready') {
    line = 'Connect your soundtrack';
    detail = 'Type /audio in game and open the link it gives you.';
  } else if (snapshot.area === null) {
    line = state.context?.online === true ? 'Quiet here' : 'Not in game';
    detail = state.context?.online === true ? 'Nothing plays where you are standing.' : 'Join the server and the music starts.';
  } else {
    line = <AreaName source={snapshot.area.displayName} />;
    detail = !enabled ? 'Tap to start the music.' : snapshot.track !== null ? `${snapshot.track.title}${snapshot.track.artist === '' ? '' : ` · ${snapshot.track.artist}`}` : '';
  }
  return (
    <Link to="/audio" className={`ph-widget dc-frame ${playing ? 'ph-widget-playing' : ''}`} data-testid="phone-now-playing" data-state={playing ? 'playing' : phase}>
      <span className="ph-widget-kicker">{playing ? 'Now playing' : 'Music'}</span>
      <span className="ph-widget-line">{line}</span>
      {detail !== '' && <span className="ph-widget-detail">{detail}</span>}
      {playing && <Visualiser source={engine?.analyser ?? null} active intense={snapshot.area?.category === 'battle'} />}
    </Link>
  );
}
